
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiGrid, FiUpload, FiRefreshCw, FiDatabase, FiInbox } from 'react-icons/fi';
import { testSapConnection } from '../../utils/grirApi';
import GRIRDashboard from './GRIRDashboard';
import GRIRUpload from './GRIRUpload';
import GRIRReconciliation from './GRIRReconciliation';
import GRIRSapSync from './GRIRSapSync';
import GRIRReceipts from './GRIRReceipts';
import GRIRChatWidget from './GRIRChatWidget';
import '../../Styles/GRIR.css';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: <FiGrid size={15} /> },
  { id: 'upload', label: 'Upload', icon: <FiUpload size={15} /> },
  { id: 'receipts', label: 'Receipts', icon: <FiInbox size={15} /> },
  { id: 'reconciliations', label: 'Reconciliations', icon: <FiRefreshCw size={15} /> },
  { id: 'sap-sync', label: 'SAP Sync', icon: <FiDatabase size={15} /> },
];

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.18, ease: 'easeIn' } },
};

const GRIRApp = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sapConnected, setSapConnected] = useState(null);

  useEffect(() => {
    testSapConnection()
      .then((data) => setSapConnected(data.all_connected))
      .catch(() => setSapConnected(false));
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard': return <GRIRDashboard onNavigate={setActiveTab} />;
      case 'upload': return <GRIRUpload />;
      case 'receipts': return <GRIRReceipts />;
      case 'reconciliations': return <GRIRReconciliation />;
      case 'sap-sync': return <GRIRSapSync />;
      default: return <GRIRDashboard onNavigate={setActiveTab} />;
    }
  };

  return (
    <motion.div
      className="grir-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      {/* ── Header ── */}
      <motion.div
        className="grir-header"
        initial={{ y: -56, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="grir-header-left">
          <motion.button
            className="grir-back-btn"
            onClick={() => navigate('/home')}
            whileTap={{ scale: 0.95 }}
          >
            <FiArrowLeft size={15} />
            <span>Back to Home</span>
          </motion.button>
          <motion.div
            className="grir-header-title"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.12, duration: 0.4 }}
          >
            <h1>GR/IR Reconciliation</h1>
            <p>Goods Receipt · Invoice Receipt · Matching Engine</p>
          </motion.div>
        </div>

        <motion.div
          className="grir-sap-indicator"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 260, damping: 20 }}
        >
          <span
            className={`grir-sap-dot ${sapConnected === null ? 'pending' : sapConnected ? 'connected' : 'disconnected'
              }`}
          />
          <span className="grir-sap-label">
            SAP&nbsp;
            {sapConnected === null ? 'Checking…' : sapConnected ? 'Connected' : 'Disconnected'}
          </span>
        </motion.div>
      </motion.div>

      {/* ── Tab bar ── */}
      <motion.div
        className="grir-tab-bar"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18, duration: 0.35 }}
      >
        {TABS.map((tab, i) => (
          <motion.button
            key={tab.id}
            className={`grir-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 + i * 0.06 }}
            whileTap={{ scale: 0.96 }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* ── Tab content ── */}
      <div className="grir-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {renderTab()}
          </motion.div>
        </AnimatePresence>
      </div>

      <GRIRChatWidget />
    </motion.div>
  );
};

export default GRIRApp;